/**
 * SDK-agnostic filtering logic for browser error-tracking events.
 *
 * Datadog's RUM browser SDK exposes no declarative `allowUrls` / `denyUrls` /
 * `ignoreErrors` options (unlike Sentry). The only hook is `beforeSend`. This
 * module reimplements those two ideas as injectable, unit-testable functions:
 *
 *   1. an origin allowlist (the equivalent of Sentry's `allowUrls`), and
 *   2. a message ignore-list (the equivalent of Sentry's `ignoreErrors`).
 *
 * It also adds a queryable `alertable` boolean attribute onto kept error events,
 * which surfaces in Datadog as `@context.alertable`.
 *
 * Nothing here imports the Datadog SDK or touches the DOM, so it can be tested
 * against a plain config object. The wiring lives in
 * `reverb-ui/src/vendor/datadogErrorTracking.ts`.
 */

export type IgnoredMessagePattern = string | RegExp;

export interface ErrorEventFilterConfig {
  /**
   * Allowlist of origins (matched against URLs found in the error's stack).
   * When non-empty, an error whose stack contains URL frames is kept only if at
   * least one of those URLs matches an allowed origin. This is the durable,
   * self-maintaining filter: new third-party / extension / ad scripts are
   * dropped automatically without adding a rule.
   *
   * When empty, origin allowlisting is skipped entirely.
   */
  allowedOrigins: RegExp[];

  /**
   * Messages we never want to record, regardless of origin. Use this for noise
   * that originates from our own origin (chunk-load failures, ResizeObserver)
   * or for injected errors whose stack has no usable URL (e.g. eval'd
   * `googletag is not defined`, which the origin allowlist cannot catch).
   */
  ignoredMessagePatterns: IgnoredMessagePattern[];

  /**
   * Messages to keep and report, but mark as non-alertable.
   * This is the opt-out denylist for the `alertable` tag.
   * An error that passes through `shouldKeepErrorEvent` is tagged
   * with `alertable: true` by default, and only set to `alertable: false`
   * when its message matches one of these patterns.
   */
  nonAlertableMessagePatterns: IgnoredMessagePattern[];
}

/**
 * Minimal structural shape of a RUM error event. Intentionally a subset of
 * Datadog's `RumEvent` union so tests need not construct a full SDK event.
 */
export interface FilterableEvent {
  type: string;
  error?: {
    message?: string;
    stack?: string;
  };
  /**
   * Custom attributes for the event. Anything `beforeSend` writes here surfaces
   * in Datadog under `@context.*`. This is the only event field the RUM SDK preserves
   * from `beforeSend`, the SDK ignores other attributes added to the event.
   */
  context?: Record<string, unknown>;
}

const URL_IN_STACK = /https?:\/\/[^\s)'"]+/g;

/**
 * Build an allowed-origin pattern that matches a stack-trace URL only when
 * `domain` is the URL's hostname (or a subdomain of it). Anchoring to the
 * scheme + host means the domain can never match inside a path or query
 * string, so a third-party URL that merely mentions our domain
 * (`https://evil.com/reverb.com/app.js`, a tracker carrying
 * `?ref=https://reverb.com/x`) or a suffix spoof (`reverb.com.evil.com`) is
 * correctly treated as third-party. The leading `^` anchor requires the match
 * to start at the scheme (extracted URLs always begin there), so a
 * fully-qualified domain embedded later in the string can't match. The trailing
 * lookahead requires a port/path delimiter or end-of-string so the host can't
 * bleed into a longer label.
 */
export function originPatternForDomain(domain: string): RegExp {
  const escapedDomain = domain.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`^https?://([a-z0-9-]+\\.)*${escapedDomain}(?=[:/]|$)`, 'i');
}

/** Extract every http(s) URL referenced in a stack-trace string. */
export function extractUrlsFromStack(stack: string | undefined): string[] {
  if (!stack) { return []; }
  // String.prototype.match resets lastIndex, so the module-level /g RegExp is
  // safe to reuse across calls.
  return stack.match(URL_IN_STACK) ?? [];
}

function matchesAnyPattern(
  message: string,
  patterns: IgnoredMessagePattern[],
): boolean {
  return patterns.some((pattern) => {
    if (typeof pattern === 'string') {
      return message.includes(pattern);
    }

    return message.match(pattern) !== null;
  });
}

function hasIgnoredMessage(
  event: FilterableEvent,
  ignoredMessagePatterns: IgnoredMessagePattern[],
): boolean {
  return matchesAnyPattern(event.error?.message ?? '', ignoredMessagePatterns);
}

/**
 * Whether a kept error should be tagged as alertable.
 * Errors are alertable by default. Only errors whose message
 * matches `nonAlertableMessagePatterns` are opted out.
 * An error without a message stays alertable.
 */
export function isAlertableError(
  event: FilterableEvent,
  nonAlertableMessagePatterns: IgnoredMessagePattern[],
): boolean {
  return !matchesAnyPattern(event.error?.message ?? '', nonAlertableMessagePatterns);
}

/**
 * True when the error may be kept on origin grounds: at least one stack frame is
 * from an allowed origin, OR there is nothing to judge it on (no allowlist
 * configured, or no URL frames to attribute the error to). Only a stack made up
 * entirely of disallowed (third-party) origins returns false.
 */
function isFromAllowedOrigin(event: FilterableEvent, allowedOrigins: RegExp[]): boolean {
  if (allowedOrigins.length === 0) {
    return true;
  }

  const urls = extractUrlsFromStack(event.error?.stack);
  if (urls.length === 0) {
    return true;
  }

  return urls.some((url) => allowedOrigins.some((origin) => url.match(origin) !== null));
}

/**
 * Decide whether an error event should be kept (true) or discarded (false).
 *
 * Rules, in order:
 *   - Non-error events are always kept.
 *   - An error whose message matches `ignoredMessagePatterns` is discarded.
 *   - If `allowedOrigins` is non-empty AND the stack contains URL frames, the
 *     error is kept only when one of those URLs matches an allowed origin.
 *   - Otherwise (no determinable origin, no message match) the error is kept.
 *     We err toward keeping so genuine first-party errors that lack URL frames
 *     are never silently dropped.
 */
export function shouldKeepErrorEvent(
  event: FilterableEvent,
  config: ErrorEventFilterConfig,
): boolean {
  if (event.type !== 'error' || !event.error) {
    return true;
  }

  if (hasIgnoredMessage(event, config.ignoredMessagePatterns)) {
    return false;
  }

  if (!isFromAllowedOrigin(event, config.allowedOrigins)) {
    return false;
  }

  return true;
}

type BeforeSendCallback = (event: FilterableEvent) => boolean;

/**
 * Build a `beforeSend` callback for `datadogRum.init`. Returning `false`
 * discards the event before it leaves the browser (so it is never ingested or
 * billed); any other return value keeps it.
 *
 * For kept error events it also adds `event.context.alertable`, which surfaces
 * in Datadog as `@context.alertable`. Non-error events carry nothing
 * to classify and are left untagged.
 */
export function createErrorEventBeforeSend(config: ErrorEventFilterConfig): BeforeSendCallback {
  return (event: FilterableEvent): boolean => {
    if (!shouldKeepErrorEvent(event, config)) {
      return false;
    }

    if (event.type === 'error') {
      event.context = {
        ...event.context,
        alertable: isAlertableError(event, config.nonAlertableMessagePatterns),
      };
    }

    return true;
  };
}
